/**
 * Form primitives — Field, Input, Textarea, Select, Checkbox, Switch.
 *
 * Every form field in the app is built from <Field> wrapping a control.
 * Field provides label, hint, error states, and required-marker consistently.
 *
 * Examples:
 *   <Field label="Số chỉ điện" hint="Nhập số trên đồng hồ" required>
 *     <Input prefix={<Zap size={14} />} suffix="kWh" inputMode="decimal" />
 *   </Field>
 *
 *   <Field label="Hình thức thanh toán" error={errors.method}>
 *     <Select options={[{value:'cash',label:'Tiền mặt'}, ...]} />
 *   </Field>
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from './icons';

// ─── Field wrapper ──────────────────────────────────────────────────────────
export function Field({
  label,
  hint,
  error,
  required,
  htmlFor,
  className = '',
  children,
}) {
  return (
    <label htmlFor={htmlFor} className={['flex flex-col gap-1.5', className].join(' ')}>
      {label && (
        <span className="text-sm font-medium text-stone-900 flex items-center gap-1">
          {label}
          {required && <span className="text-red-600" aria-label="required">*</span>}
        </span>
      )}
      {children}
      {hint && !error && <span className="text-xs text-stone-500">{hint}</span>}
      {error && <span className="text-xs text-red-600" role="alert">{error}</span>}
    </label>
  );
}

// ─── Input ──────────────────────────────────────────────────────────────────
const inputBase =
  'flex items-stretch w-full bg-white border border-stone-300 rounded-md overflow-hidden ' +
  'transition-colors focus-within:border-teal-600 focus-within:ring-1 focus-within:ring-teal-600 ' +
  'hover:border-stone-400 data-[invalid=true]:border-red-500 data-[invalid=true]:focus-within:ring-red-500 ' +
  'data-[disabled=true]:bg-stone-50 data-[disabled=true]:cursor-not-allowed';

const inputSizes = {
  sm: 'h-8  text-xs',
  md: 'h-9  text-sm',
  lg: 'h-10 text-base',
};

export const Input = React.forwardRef(function Input(
  { prefix, suffix, size = 'md', invalid, disabled, className = '', inputClassName = '', ...rest },
  ref
) {
  return (
    <div
      data-invalid={invalid || undefined}
      data-disabled={disabled || undefined}
      className={[inputBase, inputSizes[size], className].join(' ')}
    >
      {prefix && (
        <span className="inline-flex items-center justify-center pl-2.5 pr-1 text-stone-400 shrink-0">
          {prefix}
        </span>
      )}
      <input
        ref={ref}
        disabled={disabled}
        className={['flex-1 min-w-0 h-full px-2.5 bg-transparent outline-none placeholder:text-stone-400', inputClassName].join(' ')}
        {...rest}
      />
      {suffix && (
        <span className="inline-flex items-center justify-center pr-2.5 pl-1 text-stone-500 shrink-0 text-sm">
          {suffix}
        </span>
      )}
    </div>
  );
});

// ─── Textarea ───────────────────────────────────────────────────────────────
export const Textarea = React.forwardRef(function Textarea(
  { invalid, disabled, rows = 3, className = '', ...rest },
  ref
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      disabled={disabled}
      className={[
        'w-full px-2.5 py-2 bg-white border border-stone-300 rounded-md text-sm leading-snug',
        'transition-colors focus:border-teal-600 focus:ring-1 focus:ring-teal-600 outline-none',
        'hover:border-stone-400 placeholder:text-stone-400',
        invalid && 'border-red-500 focus:border-red-500 focus:ring-red-500',
        disabled && 'bg-stone-50 cursor-not-allowed',
        className,
      ].filter(Boolean).join(' ')}
      {...rest}
    />
  );
});

// ─── Select ─────────────────────────────────────────────────────────────────
export const Select = React.forwardRef(function Select(
  { options = [], size = 'md', invalid, disabled, className = '', placeholder, ...rest },
  ref
) {
  const sizeCls = inputSizes[size];
  return (
    <div className={[inputBase, sizeCls, 'relative', className].join(' ')}
         data-invalid={invalid || undefined} data-disabled={disabled || undefined}>
      <select
        ref={ref}
        disabled={disabled}
        style={{ WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none' }}
        className="flex-1 min-w-0 w-full h-full px-2.5 pr-8 bg-transparent outline-none cursor-pointer disabled:cursor-not-allowed"
        {...rest}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(o => (
          <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>
        ))}
      </select>
      <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
    </div>
  );
});

// ─── Checkbox ───────────────────────────────────────────────────────────────
export const Checkbox = React.forwardRef(function Checkbox(
  { label, disabled, className = '', ...rest },
  ref
) {
  return (
    <label className={['inline-flex items-center gap-2 cursor-pointer group',
      disabled && 'opacity-50 cursor-not-allowed', className].filter(Boolean).join(' ')}>
      <input
        ref={ref}
        type="checkbox"
        disabled={disabled}
        className="w-4 h-4 rounded border-stone-300 text-teal-600 focus:ring-teal-600 focus:ring-offset-0"
        {...rest}
      />
      {label && <span className="text-sm text-stone-700 group-hover:text-stone-900">{label}</span>}
    </label>
  );
});

// ─── Switch (toggle) ────────────────────────────────────────────────────────
export function Switch({ checked, onChange, label, disabled, className = '' }) {
  return (
    <label className={['inline-flex items-center gap-2 cursor-pointer',
      disabled && 'opacity-50 cursor-not-allowed', className].filter(Boolean).join(' ')}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange?.(!checked)}
        className={[
          'ams-focus-ring relative inline-flex w-9 h-5 shrink-0 rounded-full transition-colors',
          checked ? 'bg-teal-600' : 'bg-stone-300',
        ].join(' ')}
      >
        <span
          className={[
            'absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform',
            checked ? 'translate-x-4' : 'translate-x-0.5',
          ].join(' ')}
        />
      </button>
      {label && <span className="text-sm text-stone-700">{label}</span>}
    </label>
  );
}

// ─── SearchInput — Input with built-in search affordance + ⌘K hint ─────────
import { Search } from './icons';

export function SearchInput({ placeholder, showShortcut = true, ...rest }) {
  const { t } = useTranslation('common');
  return (
    <Input
      prefix={<Search size={13} />}
      suffix={showShortcut && (
        <kbd className="mono text-2xs px-1.5 py-0.5 bg-white border border-stone-200 rounded text-stone-500">⌘K</kbd>
      )}
      placeholder={placeholder ?? t('form.search')}
      {...rest}
    />
  );
}
